import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Typography,
  Paper,
  Box,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { withTranslation } from 'react-i18next';
import GROUP_TYPES from '../common/group-types';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  columnVisualization: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    marginTop: theme.spacing(2),
    minHeight: '100px',
    overflowX: 'auto',
    overflowY: 'hidden',
    '&::-webkit-scrollbar': {
      height: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '3px',
    },
  },
  expansionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fff',
    minWidth: '140px',
    flexShrink: 0,
  },
  expansionLabel: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  columnGroup: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    overflowX: 'auto',
    padding: theme.spacing(0.5),
    '&::-webkit-scrollbar': {
      height: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '2px',
    },
  },
  columnItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: '80px',
    flexShrink: 0,
  },
  columnChip: {
    cursor: 'pointer',
    fontSize: '0.75rem',
    width: '100%',
    '&:hover': {
      opacity: 0.8,
      transform: 'scale(1.05)',
    },
    transition: 'all 0.2s',
  },
  columnSelector: {
    minWidth: '100%',
    fontSize: '0.7rem',
    '& .MuiSelect-select': {
      padding: '4px 8px',
      fontSize: '0.7rem',
    },
  },
  mainUnitGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: '2px solid',
    borderColor: theme.palette.primary.main,
    borderRadius: '4px',
    backgroundColor: '#e3f2fd',
    minWidth: '160px',
    flexShrink: 0,
  },
  mainUnitLabel: {
    fontWeight: 'bold',
    fontSize: '1rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.5),
  },
  arrowContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0, 1),
    color: theme.palette.text.secondary,
    fontSize: '1.5rem',
  },
  groupSelector: {
    minWidth: 150,
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
  },
  assignedPharmacy: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    border: '1px solid #1976d2',
  },
  assignedOptical: {
    backgroundColor: '#fff3e0',
    color: '#f57c00',
    border: '1px solid #f57c00',
  },
  assignedEcommerce: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
    border: '1px solid #7b1fa2',
  },
  unassigned: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
    border: '1px solid #ddd',
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
});

class ColumnVisualization extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openSelectId: null, // Track which select dropdown is open
    };
  }

  getGroupName = (groupTypeId) => {
    if (!groupTypeId) return 'Unassigned';
    if (groupTypeId === GROUP_TYPES.Pharmacy) return 'Pharmacy';
    if (groupTypeId === GROUP_TYPES.Retail) return 'Optical';
    if (groupTypeId === GROUP_TYPES.eCommerce) return 'eCommerce';
    return 'Unknown';
  };

  getColumnChipColor = (columnId) => {
    const { columnMappings } = this.props;
    
    // Find which group this column belongs to
    for (const [groupTypeId, columns] of Object.entries(columnMappings)) {
      if (columns.includes(columnId)) {
        if (groupTypeId === GROUP_TYPES.Pharmacy) return 'assignedPharmacy';
        if (groupTypeId === GROUP_TYPES.Retail) return 'assignedOptical';
        if (groupTypeId === GROUP_TYPES.eCommerce) return 'assignedEcommerce';
      }
    }
    return 'unassigned';
  };

  handleColumnGroupChange = (columnId, event) => {
    const { onColumnGroupChange } = this.props;
    const groupTypeId = event.target.value === 'none' ? null : event.target.value;
    if (onColumnGroupChange) {
      onColumnGroupChange(columnId, groupTypeId);
    }
  };

  renderColumnItem = (column) => {
    const { classes, isSuperAdmin, userGroupType, columnMappings } = this.props;
    
    console.log(`[ColumnVisualization] renderColumnItem for column: ${column.id} (${column.column})`);
    console.log(`[ColumnVisualization] columnMappings:`, columnMappings);
    
    // Find which group this column belongs to
    const currentGroup = Object.keys(columnMappings || {}).find((groupId) => {
      const columns = columnMappings[groupId];
      return columns && Array.isArray(columns) && columns.includes(column.id);
    });
    
    console.log(`[ColumnVisualization] currentGroup for ${column.id}:`, currentGroup);
    
    const chipColorClass = this.getColumnChipColor(column.id);
    // Column is unassigned if currentGroup is undefined, null, or empty string
    const isUnassigned = !currentGroup || currentGroup === '' || currentGroup === 'none';
    
    console.log(`[ColumnVisualization] isUnassigned for ${column.id}:`, isUnassigned);
    
    // Build available groups list
    const availableGroupsMap = new Map();
    
    // Strict check: isSuperAdmin must be explicitly true
    const isUserSuperAdmin = isSuperAdmin === true;
    
    console.log(`[ColumnVisualization] Building availableGroups for ${column.id}`, {
      isSuperAdmin,
      isUserSuperAdmin,
      isUnassigned,
      userGroupType,
      userGroupTypeType: typeof userGroupType,
      userGroupTypeValue: userGroupType
    });
    
    if (isUserSuperAdmin) {
      // Super admin can assign to any group - always show all groups
      availableGroupsMap.set(GROUP_TYPES.Pharmacy, 'Pharmacy');
      availableGroupsMap.set(GROUP_TYPES.Retail, 'Optical');
      availableGroupsMap.set(GROUP_TYPES.eCommerce, 'eCommerce');
      console.log(`[ColumnVisualization] User is Super Admin - showing all groups:`, Array.from(availableGroupsMap.keys()));
    } else {
      // For non-super admin users, only show their own group type
      // If group is assigned to the logged in user and it belongs to pharmacy, show pharmacy option only
      // If group is assigned to the logged in user and it belongs to retail, show retail option only
      // If group is assigned to the logged in user and it belongs to ecommerce, show ecommerce option only
      if (userGroupType && userGroupType !== 'none' && userGroupType !== 'Unassigned' && userGroupType.trim && userGroupType.trim() !== '') {
        // User has a group assigned - only show that group type
        const groupName = this.getGroupName(userGroupType);
        availableGroupsMap.set(userGroupType, groupName);
        console.log(`[ColumnVisualization] Non-super admin user with group assigned (${userGroupType} - ${groupName}), showing only that group`);
      } else {
        // If no userGroupType, don't show any groups (user cannot assign columns without a group)
        console.log(`[ColumnVisualization] No valid userGroupType found (value: ${userGroupType}), user cannot assign columns to any group`);
      }
    }
    
    // Always add "Unassigned" option so user can unassign at any time
    availableGroupsMap.set('none', 'Unassigned');
    console.log(`[ColumnVisualization] Added 'Unassigned' option for ${column.id}`);
    
    // Convert map to array, ensuring no duplicates
    console.log(
      `[ColumnVisualization] availableGroupsMap before conversion for ${column.id}:`,
      Array.from(availableGroupsMap.entries())
    );

    let availableGroups = Array.from(availableGroupsMap.entries()).map(
      ([id, name]) => ({
        id,
        name,
      })
    );
    
    // CRITICAL: Final safeguard - if user is not super admin and has a userGroupType,
    // ONLY show that group type plus the 'Unassigned' option (to allow unassign)
    if (!isUserSuperAdmin && userGroupType && userGroupType !== 'none' && userGroupType !== 'Unassigned') {
      // Normalize userGroupType for comparison (handle string trimming)
      const normalizedUserGroupType = typeof userGroupType === 'string' ? userGroupType.trim() : userGroupType;
      const beforeFilter = availableGroups.length;
      availableGroups = availableGroups.filter((g) => {
        // Normalize group id for comparison
        const normalizedGroupId =
          typeof g.id === 'string' ? g.id.trim() : g.id;
        // Keep only the user's group type and 'none' (always allow unassign)
        const keep =
          normalizedGroupId === normalizedUserGroupType ||
          g.id === 'none' ||
          normalizedGroupId === 'none';
        if (!keep) {
          console.log(
            `[ColumnVisualization] Final safeguard: Removing group ${g.id} (${g.name}) - user can only see ${normalizedUserGroupType} or unassign`
          );
        }
        return keep;
      });
      const afterFilter = availableGroups.length;
      if (beforeFilter !== afterFilter) {
        console.log(`[ColumnVisualization] Final safeguard filter: Before: ${beforeFilter}, After: ${afterFilter} for ${column.id}`);
      }
    }

    console.log(`[ColumnVisualization] Final availableGroups for ${column.id}:`, availableGroups);

    // Determine the select value - must be one of the available groups
    // If column is assigned, use currentGroup; if unassigned, use 'none' only if it's in availableGroups
    let selectValue = '';
    if (currentGroup && availableGroups.find(g => g.id === currentGroup)) {
      selectValue = currentGroup;
      console.log(`[ColumnVisualization] selectValue set to currentGroup: ${selectValue} for ${column.id}`);
    } else if (isUnassigned && availableGroups.find(g => g.id === 'none')) {
      selectValue = 'none';
      console.log(`[ColumnVisualization] selectValue set to 'none' (unassigned) for ${column.id}`);
    } else if (availableGroups.length > 0) {
      selectValue = availableGroups[0].id;
      console.log(`[ColumnVisualization] selectValue set to first available: ${selectValue} for ${column.id}`);
    } else {
      console.warn(`[ColumnVisualization] No selectValue could be determined for ${column.id}`);
    }
    
    console.log(`[ColumnVisualization] Final selectValue for ${column.id}:`, selectValue);

    // Create a unique ID for the select to target it
    const selectId = `select-${column.id}`;
    const isOpen = this.state.openSelectId === column.id;

    return (
      <Box key={column.id} className={classes.columnItem}>
        <Tooltip
          title={`${column.column}: ${this.getGroupName(currentGroup)}`}
          arrow
        >
          <Chip
            label={column.column}
            size="small"
            className={`${classes.columnChip} ${classes[chipColorClass]}`}
            onClick={(e) => {
              // Open the dropdown when chip is clicked
              console.log(`[ColumnVisualization] Chip clicked for column: ${column.id} (${column.column})`);
              e.stopPropagation();
              this.setState({ openSelectId: column.id }, () => {
                console.log(`[ColumnVisualization] State updated, openSelectId: ${this.state.openSelectId}`);
              });
            }}
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
        <FormControl size="small" className={classes.columnSelector} fullWidth>
          <Select
            id={selectId}
            open={isOpen}
            onOpen={() => {
              console.log(`[ColumnVisualization] Select opened for column: ${column.id}`);
              this.setState({ openSelectId: column.id });
            }}
            onClose={() => {
              console.log(`[ColumnVisualization] Select closed for column: ${column.id}`);
              this.setState({ openSelectId: null });
            }}
            value={selectValue}
            onChange={(e) => {
              console.log(`[ColumnVisualization] Select onChange for column ${column.id}, new value:`, e.target.value);
              this.handleColumnGroupChange(column.id, e);
              this.setState({ openSelectId: null });
            }}
          >
            {availableGroups.map((group, index) => (
              <MenuItem 
                key={`group-${group.id}-${index}-col-${column.id}`} 
                value={group.id}
              >
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  renderExpansionGroup = (expansionData, side) => {
    const { classes, boxTypes } = this.props;
    const { expansion, columns } = expansionData;

    if (!columns || columns.length === 0) return null;

    // Try multiple fields to get the expansion name
    // Check for serial_number, box_name, name, label, or nested boxes_type.name
    // If expansion_id exists, look up the box type name from boxTypes
    let expansionName = 
      expansion.serial_number || 
      expansion.box_name || 
      expansion.name || 
      expansion.label ||
      (expansion.boxes_type && expansion.boxes_type.name) ||
      (expansion.box_type && expansion.box_type.name);
    
    // If still no name, try to look up from boxTypes using expansion_id
    if (!expansionName && expansion.expansion_id && boxTypes && boxTypes.length > 0) {
      const boxType = boxTypes.find(type => type.id === expansion.expansion_id);
      if (boxType) {
        expansionName = boxType.label;
      }
    }
    
    // Fallback to position-based name
    if (!expansionName) {
      expansionName = `Expansion ${Math.abs(expansion.box_position || 1)}`;
    }

    return (
      <Box key={expansion.id} className={classes.expansionGroup}>
        <Typography className={classes.expansionLabel}>
          {expansionName}
        </Typography>
        <Box className={classes.columnGroup}>
          {columns.map((column) => this.renderColumnItem(column))}
        </Box>
      </Box>
    );
  };

  renderMainUnit = () => {
    const { classes, t } = this.props;
    const { columns } = this.props;
    const mainUnitColumns = columns.mainUnit || [];

    if (mainUnitColumns.length === 0) return null;

    return (
      <Box className={classes.mainUnitGroup}>
        <Typography className={classes.mainUnitLabel}>
          {t('Location.ColumnMapping.MainUnit')}
        </Typography>
        <Box className={classes.columnGroup}>
          {mainUnitColumns.map((column) => this.renderColumnItem(column))}
        </Box>
      </Box>
    );
  };

  render() {
    const { classes, t, columns } = this.props;

    if (!columns || (!(columns.leftExpansions && columns.leftExpansions.length) && !(columns.mainUnit && columns.mainUnit.length) && !(columns.rightExpansions && columns.rightExpansions.length))) {
      return (
        <Paper className={classes.columnSection}>
          <Typography variant="body2" color="textSecondary">
            {t('Location.ColumnMapping.ColumnVisualizationPlaceholder')}
          </Typography>
        </Paper>
      );
    }

    const leftExpansions = columns.leftExpansions || [];
    const rightExpansions = columns.rightExpansions || [];

    return (
      <Box className={classes.root}>
        <Typography variant="h6" className={classes.sectionTitle} gutterBottom>
          {t('Location.ColumnMapping.ColumnHierarchy')}
        </Typography>

        <Paper className={classes.columnVisualization}>
          {/* Left Expansions */}
          {leftExpansions.length > 0 && (
            <React.Fragment>
              {leftExpansions.map((expansionData, index) => (
                <React.Fragment key={expansionData.expansion.id}>
                  {this.renderExpansionGroup(expansionData, 'left')}
                  {index < leftExpansions.length - 1 && (
                    <Box className={classes.arrowContainer}>
                      <ArrowBackIcon />
                    </Box>
                  )}
                </React.Fragment>
              ))}
              {leftExpansions.length > 0 && (
                <Box className={classes.arrowContainer}>
                  <ArrowBackIcon />
                </Box>
              )}
            </React.Fragment>
          )}

          {/* Main Unit */}
          {this.renderMainUnit()}

          {/* Right Expansions */}
          {rightExpansions.length > 0 && (
            <React.Fragment>
              <Box className={classes.arrowContainer}>
                <ArrowForwardIcon />
              </Box>
              {rightExpansions.map((expansionData, index) => (
                <React.Fragment key={expansionData.expansion.id}>
                  {this.renderExpansionGroup(expansionData, 'right')}
                  {index < rightExpansions.length - 1 && (
                    <Box className={classes.arrowContainer}>
                      <ArrowForwardIcon />
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          )}
        </Paper>

        {/* Legend */}
        <Box mt={2} display="flex" gap={2} flexWrap="wrap">
          <Chip
            label="Pharmacy"
            size="small"
            className={classes.assignedPharmacy}
          />
          <Chip
            label="Optical"
            size="small"
            className={classes.assignedOptical}
          />
          <Chip
            label="eCommerce"
            size="small"
            className={classes.assignedEcommerce}
          />
          <Chip
            label="Unassigned"
            size="small"
            className={classes.unassigned}
          />
        </Box>
      </Box>
    );
  }
}

ColumnVisualization.propTypes = {
  columns: PropTypes.shape({
    leftExpansions: PropTypes.array,
    mainUnit: PropTypes.array,
    rightExpansions: PropTypes.array,
  }),
  columnMappings: PropTypes.object.isRequired,
  isSuperAdmin: PropTypes.bool,
  userGroupType: PropTypes.string,
  boxTypes: PropTypes.array,
  onColumnGroupChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default withStyles(styles)(withTranslation()(ColumnVisualization));




// WEBPACK FOOTER //
// ./src/components/box-list/ColumnVisualization.js